<script setup>
  import {computed, onMounted, onUnmounted, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import {usePage} from "@inertiajs/vue3";
  import MergeAccountShowStepsFinished from "@pages/MergeAccount/Components/ShowStepsFinished.vue";
  import MergeAccountShowStepsIntro from "@pages/MergeAccount/Components/ShowStepsIntro.vue";
  import MergeAccountShowStepsPrivacyPassword from "@pages/MergeAccount/Components/ShowStepsPrivacyPassword.vue";
  import MergeAccountShowStepsProcessing from "@pages/MergeAccount/Components/ShowStepsProcessing.vue";
  import {MedButton} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";
  import Crypt from "@/resources/js/crypt.js";

  defineOptions({name: "PagesMergeAccountShow"});

  const pageProperties = usePage().props;

  const status = {
    INTRO: "intro",
    PRIVACY_PASSWORD: "privacy",
    READY_TO_PROCESS: "ready_to_process",
    PROCESSING: "processing",
    FINISHED: "finished",
  };

  const currentStatus = ref(status.INTRO);
  const pageIsActive = ref(false);

  const sourceKey = ref(null);
  const targetKey = ref(null);

  const processingPercentage = ref(0);

  const estimatedTime = computed(() => {
    const patientCount = pageProperties?.source?.patient_count ?? 0;
    const recordCount = pageProperties?.source?.record_count ?? 0;
    const totalCount = patientCount + recordCount;

    // 50 datasets require 30 seconds
    return Math.ceil(((totalCount / 50) * 30) / 60);
  });

  onMounted(() => {
    pageIsActive.value = true;

    if (pageProperties.transferStatus === "completed") {
      currentStatus.value = status.FINISHED;
    } else if (pageProperties.transferStatus === "fetched") {
      currentStatus.value = status.PRIVACY_PASSWORD;
    }
  });

  onUnmounted(() => {
    pageIsActive.value = false;
    if (is(status.PROCESSING)) {
      console.warn("Der aktuell laufende Migrationsprozess wird unterbrochen.");
    }
  });

  function is(status) {
    return status === currentStatus.value;
  }

  function setKeys(keys) {
    if (keys.sourceKey && keys.targetKey) {
      sourceKey.value = keys.sourceKey;
      targetKey.value = keys.targetKey;
      currentStatus.value = status.READY_TO_PROCESS;
    }
  }

  async function reEncryptValue(value) {
    try {
      // decrypt mit keySource
      const temporaryCryptDecrypt = new Crypt(sourceKey.value);
      const plaintext = await temporaryCryptDecrypt.decrypt(value);

      // encrypt mit keyTarget
      const temporaryCryptEncrypt = new Crypt(targetKey.value);
      return await temporaryCryptEncrypt.encrypt(plaintext);
    } catch (error) {
      console.error("Fehler bei der Neuverschlüsselung:", error);
      throw error; // Fehler weitergeben, damit er behandelt werden kann
    }
  }

  async function reEncryptObjectValues(object) {
    if (object === null || object === undefined) {
      return null;
    }
    const entries = Object.entries(object);
    const reEncryptedEntries = await Promise.all(
      entries.map(async ([key, value]) => [key, await reEncryptValue(value)]),
    );

    return Object.fromEntries(reEncryptedEntries);
  }

  function startProcessing() {
    currentStatus.value = status.PROCESSING;
    useAuthHttp().put(route("merge-account.update", {transfer: pageProperties.transferId}), {
      onSuccess: (response) => {
        processChunks(response.chunk);
      },
    });
  }

  async function processChunks(initialChunk = []) {
    // chunk will be altered
    let chunk = initialChunk;

    while (chunk && chunk.length > 0) {
      try {
        if (pageIsActive.value === false) {
          // exit while loop when page is not active anymore
          throw new Error("Seite wurde verlassen.");
        }

        // re-encrypt payloads from all chunks
        const processedChunk = await Promise.all(
          chunk.map(async (element) => ({
            ...element,
            payload: await reEncryptObjectValues(element.payload),
          })),
        );

        if (pageIsActive.value === false) {
          // exit while loop when page is not active anymore
          throw new Error("Seite wurde verlassen.");
        }

        // save re-encrypted chunk and receive new chunk
        const response = await useAuthHttp({chunk: processedChunk}).put(
          route("merge-account.update", {transfer: pageProperties.transferId}),
        );

        // read new chunk from response
        chunk = response?.chunk || []; // if not, exit while loop
        processingPercentage.value = Number.parseInt(response?.percentage);
      } catch (error) {
        console.error("Fehler beim Verarbeiten des Datenpakets.", error);
        break; // break while
      }
    }

    currentStatus.value = status.FINISHED;
  }
</script>

<template>
  <div id="p4umc-merge-account" class="mb-4 max-w-(--breakpoint-xl)">
    <ComponentCard title="Accounts verknüpfen" :test-id="getTestId()">
      <section v-if="is(status.INTRO)">
        <MergeAccountShowStepsIntro />

        <p class="mt-4">
          Geschätzte Verknüpfungsdauer:
          <strong class="font-semibold">{{ estimatedTime }} Minute(n)</strong>
          <br />
          Wenn Sie die Zeit jetzt nicht haben, starten Sie den Vorgang bitte zu einem späteren Zeitpunkt.
        </p>

        <p class="mt-4">
          Im nächsten Schritt werden die
          <strong class="font-semibold">Datenschutzpasswörter beider Accounts</strong>
          benötigt.
        </p>

        <div class="mt-4 flex justify-center">
          <MedButton
            variant="primary"
            :data-test="getTestId('continue-merge-button')"
            @click="currentStatus = status.PRIVACY_PASSWORD"
          >
            Weiter
          </MedButton>
        </div>
      </section>

      <section v-else-if="is(status.PRIVACY_PASSWORD) || is(status.READY_TO_PROCESS)">
        <p class="mt-4">
          Bitte geben Sie nun das
          <span v-if="$page.props.target.third_party_type === 'p4u'" class="font-semibold">
            Datenschutzpasswort des pharma4u-Accounts
          </span>
          <span v-else class="font-semibold">Datenschutzpasswort des Warenwirtschaftsaccounts</span>
          ein.
        </p>

        <MergeAccountShowStepsPrivacyPassword @ready="setKeys" />

        <ComponentAlert type="info" class="mt-4">
          Geschätzte Verknüpfungsdauer:
          <strong class="font-semibold">{{ estimatedTime }} Minute(n)</strong>
          <br />
          Wenn Sie die Zeit jetzt nicht haben, starten Sie den Vorgang bitte zu einem späteren Zeitpunkt.
          <br />
          Nach Klick auf
          <span v-if="$page.props.transferStatus === 'pending'">„Verknüpfung starten“</span>
          <span v-else>„Verknüpfung fortsetzen“</span>
          kann der Vorgang nicht mehr unterbrochen werden.
        </ComponentAlert>

        <div class="mt-4 flex justify-center">
          <MedButton
            variant="primary"
            :disabled="!is(status.READY_TO_PROCESS)"
            :data-test="getTestId('start-processing-button')"
            @click="startProcessing"
          >
            <template v-if="$page.props.transferStatus === 'pending'">Verknüpfung starten</template>
            <template v-else>Verknüpfung fortsetzen</template>
          </MedButton>
        </div>
      </section>

      <section v-else-if="is(status.PROCESSING)">
        <MergeAccountShowStepsProcessing :processing-percentage="processingPercentage" />
      </section>

      <section v-else-if="is(status.FINISHED)">
        <MergeAccountShowStepsFinished />

        <div class="mt-4 flex justify-center">
          <MedButton as="inertia" variant="primary" href="/" :data-test="getTestId('go-to-dashboard-button')">
            Zum Dashboard
          </MedButton>
        </div>
      </section>
    </ComponentCard>
  </div>
</template>
